import type { FC } from "react";
import { useId } from "react";

import { useStoreLocatorContext } from "@locators/components/shared/hooks";
import type { StoreLocatorFilterItem } from "@locators/components/shared/types";

import { Button } from "@jti/core-components/components/Button";
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "@jti/ui/components/Drawer";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { StoreLocatorFiltersAll } from "./StoreLocatorFiltersAll";

import styles from "./StoreLocatorFiltersModal.module.scss";

type StoreLocatorFiltersDrawer = {
  changeFilters?: (category: string, filter: StoreLocatorFilterItem) => void;
  checkFiltersStatus?: (category: string, filerKey: string) => boolean;
  applyFilters?: () => void;
};

export const StoreLocatorFiltersDrawer: FC<StoreLocatorFiltersDrawer> = ({
  applyFilters,
  changeFilters,
  checkFiltersStatus,
}) => {
  const allFiltersTitleId = useId();
  const { __ } = useTranslations();
  const { selectedFiltersNumber } = useStoreLocatorContext();

  const buttonTranslationKey =
    selectedFiltersNumber > 0 ? "storelocator.filters.button.with_number" : "storelocator.filters.button";

  return (
    <>
      <DrawerTrigger>
        <Button variant="text" size="sm">
          {__(buttonTranslationKey, [selectedFiltersNumber])}
        </Button>
      </DrawerTrigger>

      <DrawerContent className={styles.filtersDrawerContent} aria-labelledby={allFiltersTitleId}>
        <DrawerHeader>
          <h2 id={allFiltersTitleId}>{__("storelocator.all_filters.title")}</h2>
        </DrawerHeader>
        <DrawerBody className={styles.filtersDrawerBody}>
          <StoreLocatorFiltersAll changeFilters={changeFilters} checkFiltersStatus={checkFiltersStatus} />
        </DrawerBody>
        <DrawerFooter>
          <DrawerCloseTrigger>
            <Button data-testid="storeLocatorShowFilters" onClick={applyFilters}>
              {__("storelocator.all_filters.show_button")}
            </Button>
          </DrawerCloseTrigger>
        </DrawerFooter>
      </DrawerContent>
      <DrawerBackdrop />
    </>
  );
};
